import React from "react";
import styles from "../Capture.module.css";
import {
  Alert,
  Button,
  ButtonVariants,
} from "@axa-fr/design-system-look-and-feel-react";

type ZoomData = {
  active: boolean;
  x: number;
  y: number;
  xAffiche: number;
  yAffiche: number;
};

type ScreenshotPreviewProps = {
  screenshot: string;
  screenshotDims: { width: number; height: number };
  zoomData: ZoomData;
  uploadError: string | null;
  setZoomData: React.Dispatch<React.SetStateAction<ZoomData>>;
  touchDevice: boolean;
  isLoading: boolean;
  onRetake: () => void;
  onValidate: () => void;
  imgRef: React.RefObject<HTMLImageElement | null>;
};

const ScreenshotPreview: React.FC<ScreenshotPreviewProps> = ({
  screenshot,
  screenshotDims,
  zoomData,
  uploadError,
  setZoomData,
  touchDevice,
  isLoading,
  onRetake,
  onValidate,
  imgRef,
}) => {
  const loupe =
    zoomData.active &&
    (() => {
      const loupeSize = window.innerWidth <= 700 ? 200 : 260;
      const zoom = 2;
      const { width: imgW, height: imgH } = screenshotDims;
      function clamp(value: number, min: number, max: number) {
        return Math.max(min, Math.min(value, max));
      }
      const offsetTouchX = 50;
      const offsetTouchY = -120;
      const leftLoupe = touchDevice
        ? zoomData.xAffiche - loupeSize / 2 + offsetTouchX
        : zoomData.xAffiche - loupeSize / 2;
      const topLoupe = touchDevice
        ? zoomData.yAffiche - loupeSize / 2 + offsetTouchY
        : zoomData.yAffiche - loupeSize / 2;
      const left = clamp(
        -zoomData.x * zoom + loupeSize / 2,
        loupeSize - imgW * zoom,
        0
      );
      const top = clamp(
        -zoomData.y * zoom + loupeSize / 2,
        loupeSize - imgH * zoom,
        0
      );

      if (uploadError) {
        <p>{uploadError}</p>;
      }

      return (
        <div
          className={styles.loupeZoom}
          style={{
            position: "absolute",
            left: leftLoupe,
            top: topLoupe,
            width: loupeSize,
            height: loupeSize,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              overflow: "hidden",
              borderRadius: "50%",
              border: "2px solid #eee",
              boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
              background: "#222",
              position: "relative",
            }}
          >
            <img
              src={screenshot}
              alt="Zoom"
              style={{
                position: "absolute",
                left,
                top,
                width: imgW * zoom,
                height: imgH * zoom,
                objectFit: "cover",
                pointerEvents: "none",
                userSelect: "none",
              }}
              draggable={false}
            />
          </div>
        </div>
      );
    })();

  return (
    <div className={styles.container}>
      <Alert
        type="information"
        title={
          touchDevice
            ? "Touchez l'image pour vérifier sa qualité."
            : "Survolez l'image pour vérifier sa qualité."
        }
      >
        {/* Assurez-vous que votre document est entièrement visible, net, lisible,
        sans reflet ni ombre, et que toutes les informations importantes
        apparaissent clairement. */}
      </Alert>
      <div className={styles.previewContainer}>
        <img
          src={screenshot}
          alt="Aperçu de la capture"
          className={styles.previewImage}
          style={{
            cursor: touchDevice ? "pointer" : "zoom-in",
            userSelect: "none",
            WebkitUserSelect: "none",
            msUserSelect: "none",
            touchAction: "none",
            WebkitTouchCallout: "none",
          }}
          ref={imgRef}
          onMouseEnter={(e) => {
            if (!touchDevice && imgRef.current) {
              const rect = imgRef.current.getBoundingClientRect();
              const scaleX =
                imgRef.current.naturalWidth / imgRef.current.clientWidth;
              const scaleY =
                imgRef.current.naturalHeight / imgRef.current.clientHeight;
              const xAffiche = e.clientX - rect.left;
              const yAffiche = e.clientY - rect.top;
              const x = xAffiche * scaleX;
              const y = yAffiche * scaleY;
              setZoomData({
                active: true,
                x,
                y,
                xAffiche,
                yAffiche,
              });
            }
          }}
          onMouseMove={(e) => {
            if (!touchDevice && zoomData.active && imgRef.current) {
              const rect = imgRef.current.getBoundingClientRect();
              const scaleX =
                imgRef.current.naturalWidth / imgRef.current.clientWidth;
              const scaleY =
                imgRef.current.naturalHeight / imgRef.current.clientHeight;
              const xAffiche = e.clientX - rect.left;
              const yAffiche = e.clientY - rect.top;
              const x = xAffiche * scaleX;
              const y = yAffiche * scaleY;
              setZoomData((z) => ({ ...z, x, y, xAffiche, yAffiche }));
            }
          }}
          onMouseLeave={() => {
            if (!touchDevice) setZoomData((z) => ({ ...z, active: false }));
          }}
          onTouchStart={(e) => {
            if (touchDevice && imgRef.current) {
              e.preventDefault();
              const touch = e.touches[0];
              const rect = imgRef.current.getBoundingClientRect();
              const scaleX =
                imgRef.current.naturalWidth / imgRef.current.clientWidth;
              const scaleY =
                imgRef.current.naturalHeight / imgRef.current.clientHeight;
              const xAffiche = touch.clientX - rect.left;
              const yAffiche = touch.clientY - rect.top;
              const x = xAffiche * scaleX;
              const y = yAffiche * scaleY;
              setZoomData({
                active: true,
                x,
                y,
                xAffiche,
                yAffiche,
              });
            }
          }}
          onTouchMove={(e) => {
            if (touchDevice && zoomData.active && imgRef.current) {
              e.preventDefault();
              const touch = e.touches[0];
              const rect = imgRef.current.getBoundingClientRect();
              const scaleX =
                imgRef.current.naturalWidth / imgRef.current.clientWidth;
              const scaleY =
                imgRef.current.naturalHeight / imgRef.current.clientHeight;
              const xAffiche = touch.clientX - rect.left;
              const yAffiche = touch.clientY - rect.top;
              const x = xAffiche * scaleX;
              const y = yAffiche * scaleY;
              setZoomData((z) => ({ ...z, x, y, xAffiche, yAffiche }));
            }
          }}
          onTouchEnd={(e) => {
            if (touchDevice) {
              e.preventDefault();
              setZoomData((z) => ({ ...z, active: false }));
            }
          }}
          onTouchCancel={(e) => {
            if (touchDevice) {
              e.preventDefault();
              setZoomData((z) => ({ ...z, active: false }));
            }
          }}
          onContextMenu={(e) => e.preventDefault()}
        />
        {loupe}
      </div>
      <div className={styles.buttonContainer}>
        <Button
          variant={ButtonVariants.tertiary}
          onClick={onRetake}
          disabled={isLoading}
        >
          Non, reprendre la photo
        </Button>

        <Button
          variant={ButtonVariants.primary}
          onClick={onValidate}
          disabled={isLoading}
        >
          {isLoading ? <div className={styles.loader}></div> : "Oui"}
        </Button>
      </div>
    </div>
  );
};

export default React.memo(ScreenshotPreview);
